import React, { useMemo } from 'react';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { ReportingContentCardFull } from "./components/ReportingContentCardFull";
import { ReportingContentCardSmall } from "./components/ReportingContentCardSmall";
import styled from "styled-components";
import { desktop, tablet } from '@shared/ui/atoms/MediaQueries';
import Colours from '@shared/ui/atoms/Colours';
import {
  canUserViewOfficeReports,
  canUserViewReportingPage,
  canUserViewTeamReports,
  isCompanyAdminUser,
  isOfficeAdminUser
} from "@shared/utils/AccessControl";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { selectConfig } from "@shared/store/config.duck";
import AccessRequired from "../components/AccessRequired";
import { useTranslation } from "react-i18next";

export default function ReportingPageV2() {
  const user = useSelector(selectCurrentUser);
  const config = useSelector(selectConfig);
  const canViewPage = useMemo(() => canUserViewReportingPage(user), [user]);
  const canViewVisitorReport = useMemo(() => (isCompanyAdminUser(user) || isOfficeAdminUser(user)) && !!config?.visitorBookingEnabled, [user, config]);
  const canViewOfficeReport = useMemo(() => canUserViewOfficeReports(user), [user]);
  const canViewTeamReport = useMemo(() => canUserViewTeamReports(user), [user]);
  const {t} = useTranslation();

  document.body.style.backgroundColor = Colours.veryLightGrey;
  return (
    <div className="management" style={{paddingTop: 24, backgroundColor: Colours.veryLightGrey}}>
      <GoBackButton url="/manage"/>
      <HeadlineLarge className="management__title">{t('reports-page.reporting')}</HeadlineLarge>
      {canViewPage ? <>
        <ReportingContentCardFull imageUrl={"/assets/images/reporting-office.svg"}
                                  title={"reports-page.office-usage-title"}
                                  isVisible={canViewOfficeReport}
                                  description={"reports-page.office-usage-description"}
                                  link={"/manage/reporting/office"} />
        <ContentCardGrid>
          <ReportingContentCardSmall imageUrl={"/assets/icons/standard/Offsite.svg"}
                                     title={"reports-page.whereabouts-title"}
                                     isVisible={canViewTeamReport}
                                     description={"reports-page.whereabouts-description"}
                                     link={"/manage/reporting/whereabouts"} />

          <ReportingContentCardSmall imageUrl={"/assets/icons/font/Users.svg"}
                                     title={"reports-page.line-reports-title"}
                                     isVisible={canViewTeamReport}
                                     description={"reports-page.line-reports-description"}
                                     link={"/manage/reporting/line-reports"}
                                     disabled={false} />

          <ReportingContentCardSmall imageUrl={"/assets/icons/font/Users.svg"}
                                     title={"reports-page.unknown-title"}
                                     isVisible={canViewTeamReport}
                                     description={"reports-page.unknown-description"}
                                     link={"/manage/reporting/unknown-whereabouts"}
                                     disabled={false} />

          <ReportingContentCardSmall imageUrl={"/assets/icons/first-aider.svg"}
                                     title={"reports-page.key-responsib.title"}
                                     isVisible={canViewTeamReport}
                                     description={"reports-page.key-responsib.description"}
                                     link={"/manage/reporting/key-responsibilities"} />

          {user?.companyEntity.enableHolidayBooking && <ReportingContentCardSmall imageUrl={"/assets/icons/standard/Holiday.svg"}
                                                                                  title={"reports-page.holiday-usage-title"}
                                                                                  isVisible={canViewTeamReport}
                                                                                  description={"reports-page.holiday-usage-title"}
                                                                                  link={"/manage/reporting/holidays"} />}

          {user?.companyEntity.officeAttendanceConfirmationEnabled && <ReportingContentCardSmall imageUrl={"/assets/icons/standard/Office.svg"}
                                                                                                 title={"reports-page.checkin-title"}
                                                                                                 isVisible={canViewOfficeReport}
                                                                                                 description={"reports-page.checkin-description"}
                                                                                                 link={"/manage/reporting/office-checkin"} />}

          <ReportingContentCardSmall imageUrl={"/assets/icons/standard/Office.svg"}
                                     title={"reports-page.car-park-utilization-title"}
                                     isVisible={canViewOfficeReport}
                                     description={"reports-page.car-park-utilization-description"}
                                     link={"/manage/reporting/car-parking-utilization"} />

          <ReportingContentCardSmall imageUrl={"/assets/icons/calendar.svg"}
                                     title={"reports-page.meeting-room-usage-title"}
                                     isVisible={isCompanyAdminUser(user)}
                                     description={"reports-page.meeting-room-usage-description"}
                                     link={"/manage/reporting/meeting-room-usage"} />

          <ReportingContentCardSmall imageUrl={"/assets/icons/font/Users.svg"}
                                     title={"reports-page.visitors-title"}
                                     isVisible={canViewVisitorReport}
                                     description={"reports-page.visitors-description"}
                                     link={"/manage/reporting/visitors"} />

          <ReportingContentCardSmall imageUrl={"/assets/icons/font/Search.svg"}
                                     title={"reports-page.audit-logs-title"}
                                     isVisible={isCompanyAdminUser(user)}
                                     description={"reports-page.audit-logs-description"}
                                     link={"/manage/audit-logs"} />
        </ContentCardGrid>
      </> : <>
        <AccessRequired visible={!canViewPage} companyAdminsRequired={true} officeAdminsRequired={true} teamAdminsRequired={true} />
      </>}
    </div>
  );
}

const ContentCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-bottom: 32px;

  @media (${tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (${desktop}) {
    grid-template-columns: repeat(3, 1fr);
  }
`
